import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ModulePermissionDto } from "../utils/rolePermissions";

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  branchId?: string;
  salesPointId: string;
  isActive: boolean;
  /** Raw grants stored on user, e.g. { balance_transfer: true } */
  featurePermissions?: Record<string, boolean>;
  /** Resolved feature keys from API (login/`me` top-level) */
  effectiveFeaturePermissions?: string[];
  /**
   * Backend RBAC matrix — source of truth for UI gates.
   * Empty means fall back to legacy ROLE_PERMISSIONS until re-login.
   */
  permissions?: ModulePermissionDto[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  login: (user: AuthUser, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateUser: (user: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      login: (user, accessToken, refreshToken) =>
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
        }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        }),
      updateUser: (userData) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        })),
    }),
    {
      name: "auth-storage",
    }
  )
);
